/**
 * Log Table Theme Configuration
 * Flexible field-agnostic color system with 10 popular color schemes
 */

export interface SeverityColors {
  debug: string;
  debugBg: string;
  info: string;
  infoBg: string;
  warn: string;
  warnBg: string;
  error: string;
  errorBg: string;
  critical: string;
  criticalBg: string;
}

export interface RowStateColors {
  background: string;
  separator: string;
  hover: string;
  highlight: string;              // Permalink deep link highlight
  highlightBorder: string;
}

export interface LogThemeColors {
  background: string;              // Terminal background
  text: string;                    // Main text color
  textMuted: string;               // Muted text (timestamps, etc.)

  // Generic field colors (not tied to specific fields)
  // Usage: field1=Time, field2=Stream, field3=Host, field4=App, field5-8=future
  fields: [string, string, string, string, string, string, string, string];

  severity: SeverityColors;
  row: RowStateColors;
}

export interface LogThemeConfig {
  id: string;
  name: string;
  description: string;
  mode: 'light' | 'dark';          // Which base theme mode it's designed for
  colors: LogThemeColors;
}

export interface LogThemePreset {
  id: string;
  name: string;
  description: string;
  lightThemeId: string;
  darkThemeId: string;
}

/**
 * 10 Popular Log Color Schemes (All Open Source/MIT)
 */
export const LOG_THEMES: Record<string, LogThemeConfig> = {
  fluxtailLight: {
    id: 'fluxtail-light',
    name: 'Fluxtail Light',
    description: 'Default Fluxtail light log workspace',
    mode: 'light',
    colors: {
      background: 'transparent',
      text: 'color-mix(in oklab, var(--color-ink) 92%, transparent)',
      textMuted: 'color-mix(in oklab, var(--color-ink) 70%, transparent)',
      fields: [
        'color-mix(in oklab, var(--color-ink) 70%, transparent)',
        'color-mix(in oklab, var(--color-ok) 80%, transparent)',
        'var(--color-line2)',
        'color-mix(in oklab, var(--color-info) 80%, transparent)',
        'color-mix(in oklab, var(--color-warn) 80%, transparent)',
        'var(--color-bad)',
        'var(--color-secondary)',
        'var(--color-muted)',
      ],
      severity: {
        debug: 'color-mix(in oklab, var(--color-ink) 56%, transparent)',
        debugBg: 'color-mix(in oklab, var(--color-ink) 6%, transparent)',
        info: 'var(--color-ok)',
        infoBg: 'color-mix(in oklab, var(--color-ok) 10%, transparent)',
        warn: 'var(--color-warn)',
        warnBg: 'color-mix(in oklab, var(--color-warn) 12%, transparent)',
        error: 'var(--color-bad)',
        errorBg: 'color-mix(in oklab, var(--color-bad) 12%, transparent)',
        critical: 'var(--color-bad)',
        criticalBg: 'color-mix(in oklab, var(--color-bad) 16%, transparent)',
      },
      row: {
        background: 'transparent',
        separator: 'color-mix(in oklab, var(--color-line) 20%, transparent)',
        hover: 'color-mix(in oklab, var(--color-panel2) 42%, transparent)',
        highlight: 'color-mix(in oklab, var(--color-panel2) 88%, transparent)',
        highlightBorder: 'color-mix(in oklab, var(--color-info) 30%, transparent)',
      },
    },
  },

  fluxtailDark: {
    id: 'fluxtail-dark',
    name: 'Fluxtail Dark',
    description: 'Default Fluxtail dark log workspace',
    mode: 'dark',
    colors: {
      background: 'transparent',
      text: 'color-mix(in oklab, white 92%, transparent)',
      textMuted: 'color-mix(in oklab, white 70%, transparent)',
      fields: [
        'color-mix(in oklab, white 70%, transparent)',
        'color-mix(in oklab, var(--color-ok) 80%, transparent)',
        'var(--color-line2)',
        'color-mix(in oklab, var(--color-info) 80%, transparent)',
        'color-mix(in oklab, var(--color-warn) 80%, transparent)',
        'var(--color-bad)',
        'var(--color-secondary)',
        'var(--color-muted)',
      ],
      severity: {
        debug: 'var(--color-line2)',
        debugBg: 'color-mix(in oklab, var(--color-line2) 12%, transparent)',
        info: 'var(--color-ok)',
        infoBg: 'color-mix(in oklab, var(--color-ok) 12%, transparent)',
        warn: 'var(--color-warn)',
        warnBg: 'color-mix(in oklab, var(--color-warn) 12%, transparent)',
        error: 'var(--color-bad)',
        errorBg: 'color-mix(in oklab, var(--color-bad) 14%, transparent)',
        critical: 'var(--color-bad)',
        criticalBg: 'color-mix(in oklab, var(--color-bad) 18%, transparent)',
      },
      row: {
        background: 'transparent',
        separator: 'color-mix(in oklab, var(--color-line) 20%, transparent)',
        hover: 'color-mix(in oklab, var(--color-panel2) 42%, transparent)',
        highlight: 'color-mix(in oklab, var(--color-panel2) 88%, transparent)',
        highlightBorder: 'color-mix(in oklab, var(--color-info) 30%, transparent)',
      },
    },
  },

  // 1. Solarized Light
  solarizedLight: {
    id: 'solarized-light',
    name: 'Solarized Light',
    description: 'Precision colors for machines and people',
    mode: 'light',
    colors: {
      background: 'rgba(250, 248, 245, 0.4)',
      text: '#657b83',
      textMuted: '#93a1a1',
      fields: [
        '#6c71c4',  // field-1: violet (timestamp)
        '#2aa198',  // field-2: cyan (stream)
        '#859900',  // field-3: green (host)
        '#b58900',  // field-4: yellow (app)
        '#cb4b16',  // field-5: orange
        '#dc322f',  // field-6: red
        '#d33682',  // field-7: magenta
        '#268bd2',  // field-8: blue
      ],
      severity: {
        debug: '#93a1a1',
        debugBg: 'rgba(147, 161, 161, 0.1)',
        info: '#268bd2',
        infoBg: 'rgba(38, 139, 210, 0.1)',
        warn: '#b58900',
        warnBg: 'rgba(181, 137, 0, 0.1)',
        error: '#dc322f',
        errorBg: 'rgba(220, 50, 47, 0.1)',
        critical: '#d33682',
        criticalBg: 'rgba(211, 54, 130, 0.15)',
      },
      row: {
        background: 'transparent',
        separator: 'rgba(0, 0, 0, 0.05)',
        hover: 'rgba(38, 139, 210, 0.06)',
        highlight: 'rgba(181, 137, 0, 0.25)',
        highlightBorder: '#b58900',
      },
    },
  },

  // 2. Solarized Dark
  solarizedDark: {
    id: 'solarized-dark',
    name: 'Solarized Dark',
    description: 'Precision colors for machines and people (dark)',
    mode: 'dark',
    colors: {
      background: 'rgba(7, 54, 66, 0.5)',
      text: '#839496',
      textMuted: '#586e75',
      fields: [
        '#6c71c4',  // violet
        '#2aa198',  // cyan
        '#859900',  // green
        '#b58900',  // yellow
        '#cb4b16',  // orange
        '#dc322f',  // red
        '#d33682',  // magenta
        '#268bd2',  // blue
      ],
      severity: {
        debug: '#586e75',
        debugBg: 'rgba(88, 110, 117, 0.15)',
        info: '#268bd2',
        infoBg: 'rgba(38, 139, 210, 0.15)',
        warn: '#b58900',
        warnBg: 'rgba(181, 137, 0, 0.15)',
        error: '#dc322f',
        errorBg: 'rgba(220, 50, 47, 0.15)',
        critical: '#d33682',
        criticalBg: 'rgba(211, 54, 130, 0.2)',
      },
      row: {
        background: 'transparent',
        separator: 'rgba(131, 148, 150, 0.1)',
        hover: 'rgba(38, 139, 210, 0.08)',
        highlight: 'rgba(181, 137, 0, 0.3)',
        highlightBorder: '#b58900',
      },
    },
  },

  // 3. Dracula
  dracula: {
    id: 'dracula',
    name: 'Dracula',
    description: 'Dark theme with vibrant colors',
    mode: 'dark',
    colors: {
      background: 'rgba(40, 42, 54, 0.5)',
      text: '#f8f8f2',
      textMuted: '#6272a4',
      fields: [
        '#bd93f9',  // purple
        '#8be9fd',  // cyan
        '#50fa7b',  // green
        '#f1fa8c',  // yellow
        '#ffb86c',  // orange
        '#ff79c6',  // pink
        '#ff5555',  // red
        '#6272a4',  // comment
      ],
      severity: {
        debug: '#6272a4',
        debugBg: 'rgba(98, 114, 164, 0.15)',
        info: '#8be9fd',
        infoBg: 'rgba(139, 233, 253, 0.15)',
        warn: '#f1fa8c',
        warnBg: 'rgba(241, 250, 140, 0.15)',
        error: '#ff5555',
        errorBg: 'rgba(255, 85, 85, 0.15)',
        critical: '#ff79c6',
        criticalBg: 'rgba(255, 121, 198, 0.2)',
      },
      row: {
        background: 'transparent',
        separator: 'rgba(68, 71, 90, 0.5)',
        hover: 'rgba(139, 233, 253, 0.08)',
        highlight: 'rgba(241, 250, 140, 0.3)',
        highlightBorder: '#f1fa8c',
      },
    },
  },

  // 4. Nord
  nord: {
    id: 'nord',
    name: 'Nord',
    description: 'Arctic-inspired color palette',
    mode: 'dark',
    colors: {
      background: 'rgba(46, 52, 64, 0.5)',
      text: '#d8dee9',
      textMuted: '#4c566a',
      fields: [
        '#b48ead',  // purple
        '#88c0d0',  // cyan
        '#a3be8c',  // green
        '#ebcb8b',  // yellow
        '#d08770',  // orange
        '#bf616a',  // red
        '#5e81ac',  // blue
        '#81a1c1',  // light blue
      ],
      severity: {
        debug: '#4c566a',
        debugBg: 'rgba(76, 86, 106, 0.15)',
        info: '#88c0d0',
        infoBg: 'rgba(136, 192, 208, 0.15)',
        warn: '#ebcb8b',
        warnBg: 'rgba(235, 203, 139, 0.15)',
        error: '#bf616a',
        errorBg: 'rgba(191, 97, 106, 0.15)',
        critical: '#d08770',
        criticalBg: 'rgba(208, 135, 112, 0.2)',
      },
      row: {
        background: 'transparent',
        separator: 'rgba(59, 66, 82, 0.5)',
        hover: 'rgba(136, 192, 208, 0.08)',
        highlight: 'rgba(235, 203, 139, 0.3)',
        highlightBorder: '#ebcb8b',
      },
    },
  },

  // 5. Gruvbox Light
  gruvboxLight: {
    id: 'gruvbox-light',
    name: 'Gruvbox Light',
    description: 'Retro groove color scheme (light)',
    mode: 'light',
    colors: {
      background: 'rgba(249, 245, 215, 0.4)',
      text: '#3c3836',
      textMuted: '#7c6f64',
      fields: [
        '#b16286',  // purple
        '#689d6a',  // aqua
        '#98971a',  // green
        '#d79921',  // yellow
        '#d65d0e',  // orange
        '#cc241d',  // red
        '#458588',  // blue
        '#076678',  // dark blue
      ],
      severity: {
        debug: '#7c6f64',
        debugBg: 'rgba(124, 111, 100, 0.1)',
        info: '#458588',
        infoBg: 'rgba(69, 133, 136, 0.1)',
        warn: '#d79921',
        warnBg: 'rgba(215, 153, 33, 0.1)',
        error: '#cc241d',
        errorBg: 'rgba(204, 36, 29, 0.1)',
        critical: '#9d0006',
        criticalBg: 'rgba(157, 0, 6, 0.15)',
      },
      row: {
        background: 'transparent',
        separator: 'rgba(60, 56, 54, 0.1)',
        hover: 'rgba(69, 133, 136, 0.08)',
        highlight: 'rgba(215, 153, 33, 0.25)',
        highlightBorder: '#d79921',
      },
    },
  },

  // 6. Gruvbox Dark
  gruvboxDark: {
    id: 'gruvbox-dark',
    name: 'Gruvbox Dark',
    description: 'Retro groove color scheme (dark)',
    mode: 'dark',
    colors: {
      background: 'rgba(29, 32, 33, 0.5)',
      text: '#ebdbb2',
      textMuted: '#a89984',
      fields: [
        '#d3869b',  // purple
        '#8ec07c',  // aqua
        '#b8bb26',  // green
        '#fabd2f',  // yellow
        '#fe8019',  // orange
        '#fb4934',  // red
        '#83a598',  // blue
        '#458588',  // dark blue
      ],
      severity: {
        debug: '#a89984',
        debugBg: 'rgba(168, 153, 132, 0.15)',
        info: '#83a598',
        infoBg: 'rgba(131, 165, 152, 0.15)',
        warn: '#fabd2f',
        warnBg: 'rgba(250, 189, 47, 0.15)',
        error: '#fb4934',
        errorBg: 'rgba(251, 73, 52, 0.15)',
        critical: '#cc241d',
        criticalBg: 'rgba(204, 36, 29, 0.2)',
      },
      row: {
        background: 'transparent',
        separator: 'rgba(168, 153, 132, 0.15)',
        hover: 'rgba(131, 165, 152, 0.08)',
        highlight: 'rgba(250, 189, 47, 0.3)',
        highlightBorder: '#fabd2f',
      },
    },
  },

  // 7. One Dark Pro
  oneDark: {
    id: 'one-dark',
    name: 'One Dark Pro',
    description: 'Sleek high-contrast theme from Atom',
    mode: 'dark',
    colors: {
      background: 'rgba(33, 37, 43, 0.5)',
      text: '#abb2bf',
      textMuted: '#5c6370',
      fields: [
        '#c678dd',  // purple
        '#56b6c2',  // cyan
        '#98c379',  // green
        '#e5c07b',  // yellow
        '#d19a66',  // orange
        '#e06c75',  // red
        '#61afef',  // blue
        '#528bff',  // bright blue
      ],
      severity: {
        debug: '#5c6370',
        debugBg: 'rgba(92, 99, 112, 0.15)',
        info: '#61afef',
        infoBg: 'rgba(97, 175, 239, 0.15)',
        warn: '#e5c07b',
        warnBg: 'rgba(229, 192, 123, 0.15)',
        error: '#e06c75',
        errorBg: 'rgba(224, 108, 117, 0.15)',
        critical: '#be5046',
        criticalBg: 'rgba(190, 80, 70, 0.2)',
      },
      row: {
        background: 'transparent',
        separator: 'rgba(92, 99, 112, 0.3)',
        hover: 'rgba(97, 175, 239, 0.08)',
        highlight: 'rgba(229, 192, 123, 0.3)',
        highlightBorder: '#e5c07b',
      },
    },
  },

  // 8. Monokai Pro
  monokai: {
    id: 'monokai',
    name: 'Monokai Pro',
    description: 'Minimalist distraction-free theme',
    mode: 'dark',
    colors: {
      background: 'rgba(34, 31, 34, 0.5)',
      text: '#fcfcfa',
      textMuted: '#727072',
      fields: [
        '#ab9df2',  // purple
        '#78dce8',  // cyan
        '#a9dc76',  // green
        '#ffd866',  // yellow
        '#fc9867',  // orange
        '#ff6188',  // red
        '#78dce8',  // cyan
        '#a9dc76',  // green
      ],
      severity: {
        debug: '#727072',
        debugBg: 'rgba(114, 112, 114, 0.15)',
        info: '#78dce8',
        infoBg: 'rgba(120, 220, 232, 0.15)',
        warn: '#ffd866',
        warnBg: 'rgba(255, 216, 102, 0.15)',
        error: '#ff6188',
        errorBg: 'rgba(255, 97, 136, 0.15)',
        critical: '#f85e84',
        criticalBg: 'rgba(248, 94, 132, 0.2)',
      },
      row: {
        background: 'transparent',
        separator: 'rgba(114, 112, 114, 0.3)',
        hover: 'rgba(120, 220, 232, 0.08)',
        highlight: 'rgba(255, 216, 102, 0.3)',
        highlightBorder: '#ffd866',
      },
    },
  },

  // 9. GitHub Light
  githubLight: {
    id: 'github-light',
    name: 'GitHub Light',
    description: 'Clean and familiar GitHub theme',
    mode: 'light',
    colors: {
      background: 'rgba(246, 248, 250, 0.4)',
      text: '#24292f',
      textMuted: '#57606a',
      fields: [
        '#8250df',  // purple
        '#0969da',  // blue
        '#1a7f37',  // green
        '#bf8700',  // yellow
        '#bc4c00',  // orange
        '#cf222e',  // red
        '#0550ae',  // dark blue
        '#218bff',  // bright blue
      ],
      severity: {
        debug: '#57606a',
        debugBg: 'rgba(87, 96, 106, 0.1)',
        info: '#0969da',
        infoBg: 'rgba(9, 105, 218, 0.1)',
        warn: '#bf8700',
        warnBg: 'rgba(191, 135, 0, 0.1)',
        error: '#cf222e',
        errorBg: 'rgba(207, 34, 46, 0.1)',
        critical: '#a40e26',
        criticalBg: 'rgba(164, 14, 38, 0.15)',
      },
      row: {
        background: 'transparent',
        separator: 'rgba(208, 215, 222, 0.5)',
        hover: 'rgba(9, 105, 218, 0.06)',
        highlight: 'rgba(191, 135, 0, 0.25)',
        highlightBorder: '#bf8700',
      },
    },
  },

  // 10. GitHub Dark
  githubDark: {
    id: 'github-dark',
    name: 'GitHub Dark',
    description: 'Clean and familiar GitHub dark theme',
    mode: 'dark',
    colors: {
      background: 'rgba(22, 27, 34, 0.5)',
      text: '#c9d1d9',
      textMuted: '#8b949e',
      fields: [
        '#d2a8ff',  // purple
        '#79c0ff',  // blue
        '#7ee787',  // green
        '#f0c541',  // yellow
        '#ffa657',  // orange
        '#ff7b72',  // red
        '#58a6ff',  // bright blue
        '#56d364',  // bright green
      ],
      severity: {
        debug: '#8b949e',
        debugBg: 'rgba(139, 148, 158, 0.15)',
        info: '#58a6ff',
        infoBg: 'rgba(88, 166, 255, 0.15)',
        warn: '#f0c541',
        warnBg: 'rgba(240, 197, 65, 0.15)',
        error: '#ff7b72',
        errorBg: 'rgba(255, 123, 114, 0.15)',
        critical: '#ff5555',
        criticalBg: 'rgba(255, 85, 85, 0.2)',
      },
      row: {
        background: 'transparent',
        separator: 'rgba(33, 38, 45, 0.8)',
        hover: 'rgba(88, 166, 255, 0.08)',
        highlight: 'rgba(240, 197, 65, 0.3)',
        highlightBorder: '#f0c541',
      },
    },
  },

  // 11. Night Owl
  nightOwl: {
    id: 'night-owl',
    name: 'Night Owl',
    description: 'Dark blue theme for night coders',
    mode: 'dark',
    colors: {
      background: 'rgba(11, 41, 66, 0.5)',
      text: '#d6deeb',
      textMuted: '#637777',
      fields: [
        '#c792ea',  // purple
        '#7fdbca',  // cyan
        '#addb67',  // green
        '#ecc48d',  // yellow
        '#f78c6c',  // orange
        '#ef5350',  // red
        '#82aaff',  // blue
        '#80cbc4',  // teal
      ],
      severity: {
        debug: '#637777',
        debugBg: 'rgba(99, 119, 119, 0.15)',
        info: '#82aaff',
        infoBg: 'rgba(130, 170, 255, 0.15)',
        warn: '#ecc48d',
        warnBg: 'rgba(236, 196, 141, 0.15)',
        error: '#ef5350',
        errorBg: 'rgba(239, 83, 80, 0.15)',
        critical: '#ff5874',
        criticalBg: 'rgba(255, 88, 116, 0.2)',
      },
      row: {
        background: 'transparent',
        separator: 'rgba(1, 22, 39, 0.5)',
        hover: 'rgba(130, 170, 255, 0.08)',
        highlight: 'rgba(236, 196, 141, 0.3)',
        highlightBorder: '#ecc48d',
      },
    },
  },

  // 12. Atom One Light
  atomLight: {
    id: 'atom-light',
    name: 'Atom One Light',
    description: 'Popular light theme from Atom editor',
    mode: 'light',
    colors: {
      background: 'rgba(250, 250, 250, 0.4)',
      text: '#383a42',
      textMuted: '#a0a1a7',
      fields: [
        '#a626a4',  // purple
        '#0184bc',  // cyan
        '#50a14f',  // green
        '#c18401',  // yellow
        '#d75f00',  // orange
        '#e45649',  // red
        '#4078f2',  // blue
        '#0997b3',  // teal
      ],
      severity: {
        debug: '#a0a1a7',
        debugBg: 'rgba(160, 161, 167, 0.1)',
        info: '#4078f2',
        infoBg: 'rgba(64, 120, 242, 0.1)',
        warn: '#c18401',
        warnBg: 'rgba(193, 132, 1, 0.1)',
        error: '#e45649',
        errorBg: 'rgba(228, 86, 73, 0.1)',
        critical: '#ca1243',
        criticalBg: 'rgba(202, 18, 67, 0.15)',
      },
      row: {
        background: 'transparent',
        separator: 'rgba(0, 0, 0, 0.08)',
        hover: 'rgba(64, 120, 242, 0.06)',
        highlight: 'rgba(193, 132, 1, 0.25)',
        highlightBorder: '#c18401',
      },
    },
  },

  // 13. Cobalt2
  cobalt2: {
    id: 'cobalt2',
    name: 'Cobalt2',
    description: 'Bold blue and yellow theme by Wes Bos',
    mode: 'dark',
    colors: {
      background: 'rgba(25, 53, 73, 0.5)',
      text: '#ffffff',
      textMuted: '#0088ff',
      fields: [
        '#ff2c70',  // pink
        '#00d9ff',  // cyan
        '#3ad900',  // green
        '#ffc600',  // yellow
        '#ff9d00',  // orange
        '#ff2c70',  // red
        '#0088ff',  // blue
        '#80ffbb',  // mint
      ],
      severity: {
        debug: '#0088ff',
        debugBg: 'rgba(0, 136, 255, 0.15)',
        info: '#00d9ff',
        infoBg: 'rgba(0, 217, 255, 0.15)',
        warn: '#ffc600',
        warnBg: 'rgba(255, 198, 0, 0.15)',
        error: '#ff2c70',
        errorBg: 'rgba(255, 44, 112, 0.15)',
        critical: '#ff0088',
        criticalBg: 'rgba(255, 0, 136, 0.2)',
      },
      row: {
        background: 'transparent',
        separator: 'rgba(0, 136, 255, 0.15)',
        hover: 'rgba(0, 217, 255, 0.08)',
        highlight: 'rgba(255, 198, 0, 0.3)',
        highlightBorder: '#ffc600',
      },
    },
  },
};

export const LOG_THEME_PRESETS: LogThemePreset[] = [
  {
    id: 'fluxtail',
    name: 'Fluxtail',
    description: 'Current Fluxtail log colors',
    lightThemeId: 'fluxtail-light',
    darkThemeId: 'fluxtail-dark',
  },
  {
    id: 'github',
    name: 'GitHub',
    description: 'Clean neutral developer palette',
    lightThemeId: 'github-light',
    darkThemeId: 'github-dark',
  },
  {
    id: 'solarized',
    name: 'Solarized',
    description: 'Precision-tuned warm contrast',
    lightThemeId: 'solarized-light',
    darkThemeId: 'solarized-dark',
  },
  {
    id: 'gruvbox',
    name: 'Gruvbox',
    description: 'Warm retro contrast',
    lightThemeId: 'gruvbox-light',
    darkThemeId: 'gruvbox-dark',
  },
];

/**
 * Get all available themes as an array
 */
export function getAllThemes(): LogThemeConfig[] {
  return Object.values(LOG_THEMES);
}

/**
 * Get themes filtered by mode (light/dark)
 */
export function getThemesByMode(mode: 'light' | 'dark'): LogThemeConfig[] {
  return getAllThemes().filter(theme => theme.mode === mode);
}

export function getAllThemePresets(): LogThemePreset[] {
  return LOG_THEME_PRESETS;
}

export function getThemePresetById(id: string): LogThemePreset | undefined {
  return LOG_THEME_PRESETS.find(preset => preset.id === id);
}

export function inferThemePresetId(lightThemeId: string, darkThemeId: string): string | null {
  return LOG_THEME_PRESETS.find(
    preset => preset.lightThemeId === lightThemeId && preset.darkThemeId === darkThemeId
  )?.id ?? null;
}

/**
 * Get theme by ID
 */
export function getThemeById(id: string): LogThemeConfig | undefined {
  return Object.values(LOG_THEMES).find(theme => theme.id === id);
}

/**
 * User-customizable theme with full CSS variable application
 */
export class CustomLogTheme {
  constructor(public config: LogThemeConfig) {}

  /**
   * Apply theme to CSS variables
   */
  apply(): void {
    if (typeof document === 'undefined') {
      return;
    }
    const roots = Array.from(document.querySelectorAll<HTMLElement>('[data-log-theme-root="true"]'));
    if (roots.length === 0) {
      this.applyToElement(document.documentElement);
      return;
    }
    roots.forEach(root => this.applyToElement(root));
  }

  private applyToElement(root: HTMLElement): void {
    const c = this.config.colors;

    // Core colors
    root.style.setProperty('--terminal-bg', c.background);
    root.style.setProperty('--terminal-text', c.text);
    root.style.setProperty('--terminal-text-muted', c.textMuted);

    // Generic field colors (field-1 through field-8)
    c.fields.forEach((color, index) => {
      root.style.setProperty(`--log-field-${index + 1}`, color);
    });

    // Severity colors
    root.style.setProperty('--log-severity-debug', c.severity.debug);
    root.style.setProperty('--log-severity-debug-bg', c.severity.debugBg);
    root.style.setProperty('--log-severity-info', c.severity.info);
    root.style.setProperty('--log-severity-info-bg', c.severity.infoBg);
    root.style.setProperty('--log-severity-warn', c.severity.warn);
    root.style.setProperty('--log-severity-warn-bg', c.severity.warnBg);
    root.style.setProperty('--log-severity-error', c.severity.error);
    root.style.setProperty('--log-severity-error-bg', c.severity.errorBg);
    root.style.setProperty('--log-severity-critical', c.severity.critical);
    root.style.setProperty('--log-severity-critical-bg', c.severity.criticalBg);

    // Row state colors
    root.style.setProperty('--log-row-background', c.row.background);
    root.style.setProperty('--log-row-separator', c.row.separator);
    root.style.setProperty('--log-row-hover', c.row.hover);
    root.style.setProperty('--log-row-highlight', c.row.highlight);
    root.style.setProperty('--log-row-highlight-border', c.row.highlightBorder);
  }

  /**
   * Load a preset theme by ID
   */
  static loadPreset(themeId: string): CustomLogTheme | null {
    const config = getThemeById(themeId);
    if (!config) return null;

    const theme = new CustomLogTheme(config);
    theme.apply();
    return theme;
  }
}
